import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import Button from '@amzn/awsui-components-console/button';
import Icon from '@amzn/awsui-components-console/icon';
import PromptInput from '@amzn/awsui-components-console/prompt-input';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { useChatbotLogic } from './useChatbotLogic';
import './FullPageChat.scss';
import { SnowmanIcon } from './SnowmanIcon';
import { WelcomeCard } from './WelcomeCard';
import { HelpSection } from './HelpSection';
import { ASSISTANT_DISPLAY_NAME, INPUT_PLACEHOLDER } from './constants';
import { MessageList } from './MessageList';
import { ScrollToBottomButton } from './ScrollToBottomButton';

export type FullPageChatProps = RouteComponentProps;

export const FullPageChat: React.FC<FullPageChatProps> = ({ history }) => {
	const {
		messages,
		inputValue,
		isTyping,
		showSuggestions,
		suggestions,
		userPhotoUrl,
		username,
		showWelcome,
		showScrollToBottom,
		messagesEndRef,
		messageListRef,
		currentView,
		setCurrentView,
		setInputValue,
		handleSendMessage,
		handleSuggestionClick,
		handleNewConversation,
		scrollToBottom,
		stopResponse,
	} = useChatbotLogic();

	const promptItems = suggestions.map((text) => ({ text, id: text }));

	// Lock page scroll when full page chat is active
	useEffect(() => {
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => { document.body.style.overflow = prev; };
	}, []);

	const navigateBack = () => {
		if (history.length <= 1) {
			history.push('/home');
		} else {
			history.goBack();
		}
	};

	/** Shrink → navigate back and signal the widget to open via sessionStorage */
	const handleShrink = () => {
		sessionStorage.setItem('chatbot-open-on-navigate', 'true');
		navigateBack();
	};

	/** Close → navigate back, widget stays closed */
	const handleClose = () => {
		sessionStorage.setItem('chatbot-open-on-navigate', 'false');
		navigateBack();
	};

	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

	return (
		<div className="fullpage-container">
			{/* Persistent left sidebar */}
			{!sidebarCollapsed && (
				<div className="fullpage-sidebar">
					<div className="fullpage-sidebar-header">
						<div className="fullpage-sidebar-header-title">
							<Icon name="menu" />
							<span className="fullpage-sidebar-menu-label">Menu</span>
						</div>
						<button
							onClick={() => setSidebarCollapsed(true)}
							className="fullpage-sidebar-collapse-btn"
							aria-label="Collapse sidebar"
						>
							<Icon name="angle-left" />
						</button>
					</div>
					{/* Nav items */}
					<nav className="fullpage-sidebar-nav">
						<button
							onClick={() => setCurrentView('chat')}
							className={`fullpage-sidebar-nav-item ${currentView === 'chat' ? 'active' : ''}`}
						>
							<Icon name="contact" />
							<span>Chat</span>
						</button>
						<button
							onClick={() => setCurrentView('help')}
							className={`fullpage-sidebar-nav-item ${currentView === 'help' ? 'active' : ''}`}
						>
							<Icon name="status-info" />
							<span>Help</span>
						</button>
					</nav>
					{/* Footer */}
					<div className="fullpage-sidebar-footer">
						<Button variant="primary" onClick={() => { handleNewConversation(); setCurrentView('chat'); }} iconName="add-plus">
							New conversation
						</Button>
					</div>
				</div>
			)}

			{/* Main chat area */}
			<div className="fullpage-main">
				{/* Header */}
				<div className="fullpage-header">
					<div className="fullpage-header-left">
						{sidebarCollapsed && (
							<button
								onClick={() => setSidebarCollapsed(false)}
								className="fullpage-header-btn"
								aria-label="Open menu"
							>
								<Icon name="menu" />
							</button>
						)}
						<SnowmanIcon size={38} />
						<h2 className="fullpage-header-title">
							{ASSISTANT_DISPLAY_NAME}
						</h2>
					</div>
					<SpaceBetween direction="horizontal" size="xs">
						<button
							onClick={handleShrink}
							className="fullpage-header-btn"
							aria-label="Minimize to widget"
							title="Minimize"
						>
							<Icon name="shrink" />
						</button>
						<button
							onClick={handleClose}
							className="fullpage-header-btn"
							aria-label="Close chat"
							title="Close"
						>
							<Icon name="close" />
						</button>
					</SpaceBetween>
				</div>

				{currentView === 'help' ? (
					<HelpSection
						onBack={() => setCurrentView('chat')}
						onPromptClick={(text) => { handleSuggestionClick(text); setCurrentView('chat'); }}
					/>
				) : (
				<>
					{/* Message list */}
					<div className="fullpage-messages-area" ref={messageListRef}>
						<div className="fullpage-messages-inner">
							{showWelcome ? (
								<WelcomeCard
									onSuggestionClick={handleSuggestionClick}
									suggestions={suggestions}
								/>
							) : (
								<MessageList
									messages={messages}
									isTyping={isTyping}
									showSuggestions={showSuggestions}
									showWelcome={showWelcome}
									username={username}
									userPhotoUrl={userPhotoUrl}
									promptItems={promptItems}
									messagesEndRef={messagesEndRef}
									handleSuggestionClick={handleSuggestionClick}
								/>
							)}
							<div ref={messagesEndRef} />
						</div>
					</div>

					{/* Scroll-to-bottom floating button */}
					{showScrollToBottom && !showWelcome && (
						<ScrollToBottomButton onClick={scrollToBottom} />
					)}

					{/* Input area */}
					<div className="fullpage-input-area">
						<div className="fullpage-input-inner">
							<PromptInput
								value={inputValue}
								onChange={({ detail }) => setInputValue(detail.value)}
								onAction={isTyping ? stopResponse : handleSendMessage}
								actionButtonIconName={isTyping ? 'stop-circle' : 'send'}
								i18nStrings={{ actionButtonAriaLabel: isTyping ? 'Stop responding' : 'Send message' }}
								ariaLabel="Message input"
								data-testid="chatbot-input"
								maxRows={6}
								minRows={1}
								placeholder={INPUT_PLACEHOLDER}
								disableActionButton={!isTyping && !inputValue.trim()}
							/>
						</div>
					</div>
				</>
				)}
			</div>
		</div>
	);
};
